import React, { useState } from 'react';
import {
  Loader2, Copy, Download, ExternalLink, Link2, Sparkles, CheckCircle2,
  MessageCircle, Send, Linkedin,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const MANUAL_CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp Channel', Icon: MessageCircle, color: 'text-green-600', openLabel: 'Abrir WhatsApp Web' },
  { id: 'telegram', label: 'Telegram', Icon: Send, color: 'text-cyan-600', openLabel: 'Abrir Telegram' },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, color: 'text-sky-700', openLabel: 'Compartir en LinkedIn' },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Share Center reutilizable (modo manual): genera una ficha visual + copy listos
 * para pegar en cualquier red. No publica automáticamente.
 */
export default function ShareCenter({ propertyId, variant }) {
  const { toast } = useToast();
  const [channel, setChannel] = useState('whatsapp');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copyText, setCopyText] = useState('');

  const meta = MANUAL_CHANNELS.find((c) => c.id === channel);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.shareDistribution(propertyId, { channel, variant });
      setResult(res);
      setCopyText(res.copy || '');
      toast({ title: 'Ficha lista', description: `Registrado como "${res.channel_label}".` });
    } catch (e) {
      toast({ title: 'Error al generar ficha', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const cardHref = () => {
    const u = result?.card_url || '';
    return u.startsWith('/') ? `${BACKEND}${u}` : u;
  };

  const copy = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: msg });
    } catch (_) {
      toast({ title: 'No se pudo copiar', variant: 'destructive' });
    }
  };

  const openShare = () => {
    const url = channel === 'whatsapp'
      ? `https://web.whatsapp.com/send?text=${encodeURIComponent(copyText)}`
      : result?.share_url;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <section className="rounded-xl border border-gray-200 p-4 bg-gray-50/60" data-testid="share-center">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-habita" />
        <h4 className="font-semibold text-habita-dark text-sm">Share Center — contenido listo para publicar manualmente</h4>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {MANUAL_CHANNELS.map(({ id, label, Icon, color }) => (
          <button
            key={id}
            data-testid={`share-channel-${id}`}
            onClick={() => { setChannel(id); setResult(null); }}
            className={`px-3 h-8 rounded-lg border-2 text-xs font-semibold flex items-center gap-1.5 ${channel === id ? 'border-habita bg-habita/5' : 'border-gray-200 opacity-60'}`}
          >
            <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
          </button>
        ))}
        <button
          onClick={generate}
          disabled={loading}
          data-testid="share-generate"
          className="ml-auto px-4 h-8 rounded-lg bg-habita text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generar ficha
        </button>
      </div>

      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4" data-testid="share-result">
          <div>
            {result.card_url ? (
              <img src={cardHref()} alt="Ficha" className="w-full rounded-lg border border-gray-200" data-testid="share-card-img" />
            ) : (
              <div className="w-full h-40 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">Sin ficha</div>
            )}
            <p className="text-[11px] text-emerald-700 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {result.channel_label}</p>
          </div>
          <div className="space-y-2">
            <textarea
              value={copyText}
              onChange={(e) => setCopyText(e.target.value)}
              rows={6}
              data-testid="share-copy-text"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => copy(copyText, 'Texto copiado')} data-testid="share-copy-btn" className="px-3 h-9 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-white">
                <Copy className="w-3.5 h-3.5" /> Copiar texto
              </button>
              <a href={cardHref()} download target="_blank" rel="noopener noreferrer" data-testid="share-download-btn" className={`px-3 h-9 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-white ${result.card_url ? '' : 'pointer-events-none opacity-40'}`}>
                <Download className="w-3.5 h-3.5" /> Descargar ficha
              </a>
              <button onClick={openShare} data-testid="share-open-btn" className="px-3 h-9 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-white">
                <ExternalLink className="w-3.5 h-3.5" /> {meta?.openLabel}
              </button>
              <button onClick={() => copy(result.short_url, 'Enlace copiado')} data-testid="share-copy-link-btn" className="px-3 h-9 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-white">
                <Link2 className="w-3.5 h-3.5" /> Copiar enlace
              </button>
            </div>
            <p className="text-[11px] text-gray-400">Adjunta la ficha descargada en WhatsApp Web y pega el texto. El enlace registra los clics del A/B.</p>
          </div>
        </div>
      )}
    </section>
  );
}
